import type _mapboxgl from 'mapbox-gl';
import type { Map, IControl } from 'mapbox-gl';
import { RefObject, startTransition, useRef } from 'react';

import { useAsyncEffect } from '~/hooks/useAsyncEffect';

/** 最初に表示される地図の情報 */
const MAP_INFO = {
  lng: 139.76,
  lat: 39.2368,
  zoom: 3.5,
};

/** mapboxのstyles @refs https://docs.mapbox.com/api/maps/styles/#classic-mapbox-styles */
const MAPBOX_STYLES_MAP = {
  dark: 'mapbox://styles/mapbox/dark-v11',
  // light: 'mapbox://styles/mapbox/light-v11',
  light: 'mapbox://styles/mapbox/streets-v12',
} satisfies Record<UseMBMapOption['theme'], string>;

type CallbackArg = {
  map: _mapboxgl.Map;
  mapboxgl: typeof _mapboxgl;
};

export type UseMBMapOption = {
  elRef: RefObject<HTMLDivElement>;
  token: string | undefined;
  theme: 'dark' | 'light';
  minZoom: number;
};

// https://ichi-commons.sentry.io/issues/4284411884/?project=4504961959854080&query=is%3Aunresolved&referrer=issue-stream&stream_index=5
export function useMBMap(option: UseMBMapOption, fn: (_: CallbackArg) => void) {
  const { elRef, token, minZoom } = option;
  const isLoadedMapRef = useRef(false);

  useAsyncEffect(async () => {
    const isReadyMap = isLoadedMapRef.current;
    const existsElementAndToken = elRef.current && token;
    if (isReadyMap || !existsElementAndToken) return;

    if (!('mapboxgl' in window)) {
      await Promise.all([
        loadLib('script', {
          src: 'https://api.mapbox.com/mapbox-gl-js/v2.10.0/mapbox-gl.js',
        }),
        loadLib('link', {
          href: 'https://api.mapbox.com/mapbox-gl-js/v2.10.0/mapbox-gl.css',
          rel: 'stylesheet',
        }),
      ]);
    }

    if (!('MapboxLanguage' in window)) {
      await loadLib('script', {
        src: 'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-language/v1.0.0/mapbox-gl-language.js',
      });
    }

    const { lng, lat, zoom } = MAP_INFO;

    // config docs: https://docs.mapbox.com/mapbox-gl-js/api/map/
    const map = new mapboxgl.Map({
      container: elRef.current,
      style: MAPBOX_STYLES_MAP[option.theme],
      center: [lng, lat],
      zoom,
      // maxZoom:
      minZoom: minZoom,
      accessToken: token,
      attributionControl: false,
    });
    if (MapboxLanguage) {
      map.addControl(new MapboxLanguage({ supportedLanguages: ['en', 'ja'] }));
    }

    fn({ map, mapboxgl: window.mapboxgl });
    isLoadedMapRef.current = true;
  });
}

async function loadLib(tagName: 'script' | 'link', attr: Record<string, string>) {
  const element = document.createElement(tagName);
  Object.entries(attr).forEach(([attrName, value]) => element.setAttribute(attrName, value));
  document.head.appendChild(element);
  await new Promise((resolve) => element.addEventListener('load', resolve));
}

/** scriptタグで読み込まれるmapbox */
declare const mapboxgl: typeof _mapboxgl;

/** scriptタグで読み込まれるmapboxのlanguage plugin */
declare class MapboxLanguage implements IControl {
  /** @see https://github.com/mapbox/mapbox-gl-language/blob/master/index.js#L19-L27 */
  constructor(options?: { supportedLanguages?: string[] });

  onAdd(map: Map): HTMLElement;
  onRemove(map: Map): void;
}
